import type { MultipleChoiceQuestion } from '@shared/domain/form';
import { LightbulbCircleOutlined } from '@mui/icons-material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { Box, Button } from '@mui/material';
import { motion } from 'framer-motion';
import { Fragment, useId } from 'react';
import ErrorMessage from '@shared/components/ErrorMessage';
import CustomTooltip from '@shared/components/Tooltip';
import { screenMdMin } from '@shared/styles';
import {
  AnswerWrapper,
  BaseQuestionText,
  BaseQuestionTextWrapper,
  BaseQuestionWrapper,
} from './shared/QuestionStyles';

interface MultipleChoiceQuestionProps {
  handleFormFieldChange: (e: any) => void;
  formFields: any;
  question: MultipleChoiceQuestion;
  errors?: any;
  isNarrow?: boolean;
  isWide?: boolean;
  disabled?: boolean;
  disabledOptions?: string[];
}

// Using shared components from QuestionStyles
export const QuestionWrapper = BaseQuestionWrapper;
export const QuestionTextWrapper = BaseQuestionTextWrapper;

// Using shared component from QuestionStyles
export const StyledQuestionText = BaseQuestionText;

function MultipleChoiceReviewQuestion(props: MultipleChoiceQuestionProps) {
  const {
    handleFormFieldChange,
    formFields,
    question,
    isNarrow,
    isWide,
    disabled,
    errors,
    disabledOptions,
  } = props;

  const groupId = useId();

  return (
    <QuestionWrapper data-error-name={question.name} data-testid={question.name}>
      <QuestionTextWrapper $isWide={isWide}>
        <StyledQuestionText id={`${groupId}-label`}>
          {question.question}
          {' '}
          {question.tooltipText && (
            <CustomTooltip
              title={question.tooltipText}
              intent="help"
              icon={<LightbulbCircleOutlined />}
            />
          )}
        </StyledQuestionText>
      </QuestionTextWrapper>
      <AnswerWrapper>
        <Box
          role="group"
          aria-labelledby={`${groupId}-label`}
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            flexDirection: 'row',
            gap: 'var(--spacing-sm)',
            alignItems: 'center',
            [`@media ${screenMdMin}`]: {
              maxWidth: '100%',
            },
          }}
        >
          {question.options.map((value, i) => {
            const isActive = formFields[question.name] === value;
            const isDisabled
              = disabled
                || (disabledOptions ? !disabledOptions.includes(value) : false);

            return (
              <Fragment key={i}>
                <motion.div
                  {...(question.conditionallyShow
                    ? {
                        initial: {
                          x: '-100%',
                          opacity: 0,
                        },
                        transition: {
                          type: 'spring',
                          stiffness: 300,
                          damping: 20,
                          delay: i * 0.1,
                        },
                      }
                    : {})}
                  animate={{
                    x: 0,
                    opacity: 1,
                  }}
                  {...(!disabled
                    && (!disabledOptions || disabledOptions.includes(value)) && {
                    whileHover: { scale: 1.05 },
                    whileTap: { scale: 0.95 },
                  })}
                >
                  <Button
                    startIcon={isActive ? <CheckRoundedIcon /> : undefined}
                    disabled={isDisabled}
                    name={question.name}
                    value={value}
                    onClick={handleFormFieldChange}
                    variant="outlined"
                    aria-pressed={isActive}
                    sx={{
                      'border': `1px solid ${
                        isActive ? '#78bbf1' : 'var(--color-grey-dark)'
                      }`,
                      'borderRadius': '999px',
                      'color': isDisabled
                        ? 'var(--color-grey-dark)'
                        : isActive
                          ? '#fff'
                          : 'var(--color-black)',
                      'textTransform': 'none',
                      'padding': isNarrow ? '6px 12px' : '8px 16px',
                      'minHeight': '36px',
                      'height': 'auto',
                      'width': 'auto',
                      'fontSize': '14px',
                      'lineHeight': 1.2,
                      'backgroundColor': isDisabled
                        ? 'var(--color-grey-light)'
                        : isActive
                          ? '#78bbf1'
                          : 'white',
                      'boxShadow': '0 2px 4px rgba(0, 0, 0, 0.08)',
                      'transition':
                        'box-shadow 0.2s ease, background-color 0.2s ease, border-color 0.2s ease, transform 0.05s ease',
                      '&:hover': {
                        boxShadow: '0 4px 10px rgba(0, 0, 0, 0.12)',
                        backgroundColor: isDisabled
                          ? 'var(--color-grey-light)'
                          : isActive
                            ? '#78bbf1'
                            : '#f7f7f7',
                      },
                      '&:focus-visible': {
                        outline: '2px solid #78bbf1',
                        outlineOffset: '2px',
                      },
                      '&.Mui-disabled': {
                        opacity: 1,
                        cursor: 'not-allowed',
                        pointerEvents: 'none',
                      },
                    }}
                  >
                    {value}
                  </Button>
                </motion.div>
              </Fragment>
            );
          })}
        </Box>
        {errors && errors[question.name] && (
          <ErrorMessage error={errors[question.name]} />
        )}
      </AnswerWrapper>
    </QuestionWrapper>
  );
}

export default MultipleChoiceReviewQuestion;
